import { useMemo, useState } from 'react';
import { IconLoader2, IconLockOpen, IconRefresh } from '@tabler/icons-react';
import MainCard from '../../../components/MainCard';
import { StatCard } from '../../../components/cards';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { getApiErrorMessage } from '../apiErrorMessage';
import { WalletHistoryPanel } from '../WalletHistoryPanel';
import {
  useReleaseWalletHold,
  useRefreshWallet,
  useWalletSummary,
  useWalletWithdrawalRisk,
  useWalletWithdrawals,
} from './useWalletData';
import {
  formatRand,
  formatWalletBalance,
  formatWalletDate,
  renderMaskedWalletVoucherHints,
  statusBadgeVariant,
} from './walletFormat';
import {
  hasWalletSelector,
  walletSelectorQuery,
  type WalletScope,
} from './walletTypes';

type BadgeVariant = React.ComponentProps<typeof Badge>['variant'];

interface WalletPaneProps {
  scope: WalletScope;
  /** Whether the signed-in operator may release withdrawal-risk holds. */
  canReleaseHold: boolean;
  /** Clears the wallet-number scope so the pane shows every wallet of the owner. */
  onShowAllWallets?: () => void;
}

/**
 * Everything wallet-related for one app user (or the app users behind one
 * legacy account), optionally narrowed to a single wallet account number.
 * Lifted out of the Customers page so the wallet can be reached on its own,
 * without a mobile account in front of it.
 */
export function WalletPane({ scope, canReleaseHold, onShowAllWallets }: WalletPaneProps) {
  const summary = useWalletSummary(scope);
  const withdrawals = useWalletWithdrawals(scope);
  const risk = useWalletWithdrawalRisk(scope);
  const release = useReleaseWalletHold(scope);
  const refresh = useRefreshWallet(scope);
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [releaseReason, setReleaseReason] = useState('');
  const [releaseError, setReleaseError] = useState('');

  const walletNumber = scope.walletAccountNumber?.trim() || null;
  const allWallets = useMemo(() => summary.data?.walletAccounts ?? [], [summary.data]);
  const wallets = useMemo(
    () =>
      walletNumber
        ? allWallets.filter((wallet) => wallet.walletAccountNumber === walletNumber)
        : allWallets,
    [allWallets, walletNumber],
  );
  const allWithdrawals = withdrawals.data?.withdrawals ?? [];
  const visibleWithdrawals = walletNumber
    ? allWithdrawals.filter(
        (withdrawal) =>
          withdrawal.wallet_account_number === walletNumber,
      )
    : allWithdrawals;
  const withdrawalCount = walletNumber
    ? withdrawals.data?.summaryByWallet
      ? (withdrawals.data.summaryByWallet[walletNumber]?.totalWithdrawals ?? 0)
      : undefined
    : withdrawals.data?.summary?.totalWithdrawals;
  const scopedWalletMissing = Boolean(
    walletNumber && summary.data && !summary.isFetching && wallets.length === 0,
  );

  const loading = summary.isFetching || withdrawals.isFetching;
  const summaryError = summary.error
    ? getApiErrorMessage(summary.error, 'Failed to load wallet summary')
    : '';
  const withdrawalsError = withdrawals.error
    ? getApiErrorMessage(withdrawals.error, 'Failed to load wallet withdrawals')
    : '';
  const riskError = risk.error
    ? getApiErrorMessage(risk.error, 'Failed to load wallet withdrawal risk')
    : '';

  const availableBalance = walletNumber
    ? wallets[0]?.balance
      ? formatWalletBalance(wallets[0].balance.available, wallets[0].balance.currency ?? 'ZAR')
      : '-'
    : formatWalletBalance(
        summary.data?.summary?.totalAvailableBalance,
        summary.data?.summary?.currency ?? 'ZAR',
      );
  const activeCount = walletNumber
    ? wallets.filter((wallet) => wallet.status === 'ACTIVE').length
    : (summary.data?.summary?.activeWalletAccountCount ?? 0);
  const totalCount = walletNumber
    ? wallets.length
    : (summary.data?.summary?.walletAccountCount ?? allWallets.length);

  if (!hasWalletSelector(scope)) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        No app user or account is selected, so there is no wallet to show.
      </div>
    );
  }

  const submitRelease = () => {
    const reason = releaseReason.trim();
    if (!reason) return;
    setReleaseError('');
    release.mutate(reason, {
      onSuccess: () => {
        setReleaseDialogOpen(false);
        setReleaseReason('');
      },
      onError: (error) =>
        setReleaseError(getApiErrorMessage(error, 'Failed to release wallet withdrawal hold')),
    });
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">Wallet</h3>
          <p className="text-sm text-muted-foreground">
            Wallet balances, complete transaction history, and withdrawal support.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          disabled={loading}
          onClick={() => void refresh()}
        >
          {loading ? (
            <IconLoader2 className="size-4 animate-spin" />
          ) : (
            <IconRefresh className="size-4" />
          )}
          Refresh
        </Button>
      </div>

      {walletNumber && (
        <Alert className="mb-4">
          <AlertDescription className="flex flex-wrap items-center gap-2">
            <span>
              Showing wallet <span className="font-mono">{walletNumber}</span> only
              {summary.data?.appUser?.username ? ` · owner ${summary.data.appUser.username}` : ''}
              {allWallets.length > 1 ? ` · ${allWallets.length - 1} other wallet(s) on this app user` : ''}
            </span>
            {onShowAllWallets && allWallets.length > 1 && (
              <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={onShowAllWallets}>
                Show all wallets
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}

      {scopedWalletMissing && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>
            Wallet {walletNumber} is not linked to this app user in Nova.
          </AlertDescription>
        </Alert>
      )}

      {(summaryError || withdrawalsError) && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{summaryError || withdrawalsError}</AlertDescription>
        </Alert>
      )}

      {summary.data?.migrationStatus?.reason && (
        <Alert className="mb-4">
          <AlertDescription>{summary.data.migrationStatus.reason}</AlertDescription>
        </Alert>
      )}

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard title="Available Balance" value={availableBalance} />
        <StatCard title="Wallet Accounts" value={`${activeCount}/${totalCount} active`} />
        <StatCard
          title="Withdrawal requests (all time)"
          value={withdrawalCount == null ? '—' : String(withdrawalCount)}
        />
      </div>

      <div className="flex flex-col gap-6">
        <MainCard>
          <div className="p-4">
            <h4 className="text-base font-semibold">Wallet Accounts</h4>
          </div>
          {wallets.length ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Account Number</TableHead>
                  <TableHead className="text-right">Available</TableHead>
                  <TableHead className="text-right">Current</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Latest Withdrawal</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {wallets.map((wallet) => (
                  <TableRow key={wallet.id}>
                    <TableCell>{wallet.name || 'Wallet account'}</TableCell>
                    <TableCell className="break-all font-mono text-xs">{wallet.walletAccountNumber}</TableCell>
                    <TableCell className="text-right">
                      {wallet.balance ? (
                        formatWalletBalance(wallet.balance.available, wallet.balance.currency ?? 'ZAR')
                      ) : (
                        <span className="text-muted-foreground">
                          {wallet.balanceUnavailableReason ?? 'Unavailable'}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {wallet.balance
                        ? formatWalletBalance(wallet.balance.balance, wallet.balance.currency ?? 'ZAR')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusBadgeVariant(wallet.status) as BadgeVariant}>{wallet.status}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {wallet.latestWithdrawal
                        ? `${formatRand(wallet.latestWithdrawal.amount)} ${wallet.latestWithdrawal.currency} - ${wallet.latestWithdrawal.status}`
                        : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              {summary.isFetching ? 'Loading wallet accounts…' : 'No Nova wallet account link found.'}
            </div>
          )}
        </MainCard>

        {scope.appUserUuid && (risk.data || riskError) && (
          <MainCard>
            <div className="flex items-start justify-between gap-4 p-4">
              <div>
                <h4 className="text-base font-semibold">Withdrawal Risk Hold</h4>
                <p className="text-sm text-muted-foreground">
                  Withdrawal-specific review state. This does not suspend the customer.
                </p>
              </div>
              <Badge variant={risk.data?.hold?.status === 'ACTIVE' ? 'destructive' : 'secondary'}>
                {risk.data?.hold?.status ?? 'CLEAR'}
              </Badge>
            </div>
            {riskError && (
              <div className="px-4 pb-4 text-sm text-destructive">{riskError}</div>
            )}
            {risk.data?.hold ? (
              <div className="space-y-3 px-4 pb-4 text-sm">
                <div>
                  <span className="font-medium">Reason codes:</span>{' '}
                  {risk.data.hold.reasonCodes.join(', ') || 'Not recorded'}
                </div>
                <div>
                  <span className="font-medium">Evidence:</span>{' '}
                  {risk.data.hold.evidence.length} signal record(s)
                </div>
                <div className="text-muted-foreground">
                  Detected {formatWalletDate(risk.data.hold.detectedAt)}
                  {risk.data.hold.releasedAt ? `; released ${formatWalletDate(risk.data.hold.releasedAt)}` : ''}
                </div>
                {risk.data.hold.releaseReason && (
                  <div className="text-muted-foreground">Release reason: {risk.data.hold.releaseReason}</div>
                )}
                {releaseError && <div className="text-destructive">{releaseError}</div>}
                {risk.data.hold.status === 'ACTIVE' && canReleaseHold && (
                  <Button type="button" variant="outline" onClick={() => setReleaseDialogOpen(true)}>
                    <IconLockOpen className="size-4" />
                    Release hold
                  </Button>
                )}
              </div>
            ) : (
              !riskError && (
                <div className="px-4 pb-4 text-sm text-muted-foreground">
                  No active withdrawal risk hold found.
                </div>
              )
            )}
          </MainCard>
        )}

        <MainCard>
          <div className="p-4">
            <h4 className="text-base font-semibold">Withdrawals</h4>
            {withdrawals.data && (
              <p className="text-sm text-muted-foreground">
                Showing {visibleWithdrawals.length}{withdrawalCount == null ? '' : ` of ${withdrawalCount}`} withdrawal requests.
                Latest {withdrawals.data.listLimit ?? 100} requests across the owner’s wallets are available here; ledger entries appear below.
              </p>
            )}
          </div>
          {visibleWithdrawals.length ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Retailer / Reference</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleWithdrawals.map((withdrawal) => (
                  <TableRow key={withdrawal.id}>
                    <TableCell className="text-xs">{formatWalletDate(withdrawal.created_at)}</TableCell>
                    <TableCell>
                      <Badge variant={statusBadgeVariant(withdrawal.status) as BadgeVariant}>{withdrawal.status}</Badge>
                      {(withdrawal.voucher_code_available || withdrawal.voucher_pin_available) &&
                        renderMaskedWalletVoucherHints(withdrawal)}
                    </TableCell>
                    <TableCell className="text-xs">
                      <p>{withdrawal.retailer || withdrawal.payout_channel || '-'}</p>
                      {withdrawal.provider_reference && (
                        <p className="font-mono text-muted-foreground">{withdrawal.provider_reference}</p>
                      )}
                      {withdrawal.failure_message && (
                        <p className="text-destructive">{withdrawal.failure_message}</p>
                      )}
                    </TableCell>
                    <TableCell className="text-right">{formatRand(withdrawal.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              {withdrawals.isFetching ? 'Loading withdrawals…' : 'No withdrawal requests in the latest records shown.'}
            </div>
          )}
        </MainCard>
      </div>

      {!summary.isFetching && !summaryError && summary.data && wallets.length > 0 && (
        <WalletHistoryPanel
          key={JSON.stringify([scope.mvno, scope.appUserUuid, scope.iCustomer, walletNumber])}
          selector={walletSelectorQuery(scope)}
          wallets={wallets}
        />
      )}

      <Dialog
        open={releaseDialogOpen}
        onOpenChange={(open) => {
          if (!release.isPending) setReleaseDialogOpen(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Release wallet withdrawal hold</DialogTitle>
            <DialogDescription>
              Record the support decision before granting the 24-hour evidence waiver.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="wallet-hold-release-reason">Reason</Label>
            <Textarea
              id="wallet-hold-release-reason"
              value={releaseReason}
              onChange={(event) => setReleaseReason(event.target.value)}
              placeholder="Explain the review outcome"
              maxLength={500}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={release.isPending}
              onClick={() => setReleaseDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={release.isPending || !releaseReason.trim()}
              onClick={submitRelease}
            >
              {release.isPending ? <IconLoader2 className="size-4 animate-spin" /> : null}
              Release hold
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
